import { useCallback, useMemo, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getChecklistStatus } from "../endpoints/user/onboarding_checklist_GET.schema";
import { completeChecklistStep } from "../endpoints/user/onboarding_checklist_complete_POST.schema";
import { dismissChecklist as dismissChecklistApi } from "../endpoints/user/onboarding_checklist_dismiss_POST.schema";
import { useAuth } from "./useAuth";
import { trackOnboardingStep } from "./analytics";

export interface ChecklistStep {
  id: string;
  label: string;
  href: string;
  completed: boolean;
}

const CHECKLIST_QUERY_KEY = ["onboarding", "checklist"];

export const ROUTE_TO_STEP: Record<string, string> = {
  historique: "tirage",
  tirage: "tirage",
  numeros: "numeros",
  numero: "numeros",
  analyses: "numeros",
  grilles: "grilles",
  labo: "labo",
  "mon-keno": "numeros_habituels",
};

export function matchRoute(pathname: string): string | null {
  const segments = pathname.split("/").filter(Boolean);
  const first = segments[0];
  if (!first) return null;
  return ROUTE_TO_STEP[first] ?? null;
}

/**
 * Hook used by OnboardingChecklist (dashboard only).
 * Data is persisted server-side via the checklist API.
 */
export function useOnboardingChecklist() {
  const { authState } = useAuth();
  const isAuthenticated = authState.type === "authenticated";
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: CHECKLIST_QUERY_KEY,
    queryFn: getChecklistStatus,
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000,
  });

  const completedSteps = data?.completedSteps ?? ["account"];
  const dismissed = data?.dismissed ?? false;

  const dismissMutation = useMutation({
    mutationFn: dismissChecklistApi,
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: CHECKLIST_QUERY_KEY });
      const prev = queryClient.getQueryData(CHECKLIST_QUERY_KEY);
      queryClient.setQueryData(CHECKLIST_QUERY_KEY, (old: typeof data) => ({
        ...old,
        dismissed: true,
      }));
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(CHECKLIST_QUERY_KEY, ctx.prev);
    },
  });

  // Ordre orienté activation : la 1re action de valeur (configurer ses numéros
  // → résultats chaque soir) est mise en tête, juste après la création du compte.
  // Les `id` restent inchangés (mappés côté serveur via ROUTE_TO_STEP) ; seuls
  // l'ordre d'affichage et les libellés (orientés bénéfice) changent.
  const steps: ChecklistStep[] = useMemo(() => [
    { id: "account", label: "Créer votre compte", href: "#", completed: completedSteps.includes("account") },
    { id: "numeros_habituels", label: "Enregistrer vos numéros → résultats chaque soir", href: "/mon-keno", completed: completedSteps.includes("numeros_habituels") },
    { id: "tirage", label: "Voir le dernier tirage et son analyse", href: "/derniers-tirages-keno/", completed: completedSteps.includes("tirage") },
    { id: "numeros", label: "Analyser un numéro", href: "/keno-statistiques/#tableau-56", completed: completedSteps.includes("numeros") },
    { id: "grilles", label: "Générer une grille", href: "/generateur-grille-keno/", completed: completedSteps.includes("grilles") },
    { id: "labo", label: "Participer au Labo", href: "/labo", completed: completedSteps.includes("labo") },
  ], [completedSteps]);

  const completedCount = steps.filter((s) => s.completed).length;
  const totalCount = steps.length;
  const allCompleted = completedCount === totalCount;

  const dismiss = useCallback(() => {
    dismissMutation.mutate();
  }, [dismissMutation]);

  const visible = isAuthenticated && !dismissed && !allCompleted;

  return { steps, completedCount, totalCount, allCompleted, visible, dismiss };
}

/**
 * Tracks route changes and marks onboarding steps as completed (server-side).
 * Must be mounted in a component that persists across navigation (e.g. GlobalContextProviders).
 */
export function useOnboardingRouteTracker() {
  const location = useLocation();
  const { authState } = useAuth();
  const isAuthenticated = authState.type === "authenticated";
  const queryClient = useQueryClient();
  const pendingRef = useRef<Set<string>>(new Set());

  const completeMutation = useMutation({
    mutationFn: completeChecklistStep,
    onMutate: async (stepId: string) => {
      await queryClient.cancelQueries({ queryKey: CHECKLIST_QUERY_KEY });
      const prev = queryClient.getQueryData<{ completedSteps: string[]; dismissed: boolean }>(CHECKLIST_QUERY_KEY);
      queryClient.setQueryData(CHECKLIST_QUERY_KEY, (old: typeof prev) => {
        if (!old) return old;
        const steps = old.completedSteps.includes(stepId) ? old.completedSteps : [...old.completedSteps, stepId];
        return { ...old, completedSteps: steps };
      });
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(CHECKLIST_QUERY_KEY, ctx.prev);
    },
    onSettled: (_data, _err, stepId) => {
      pendingRef.current.delete(stepId);
    },
  });

  useEffect(() => {
    if (!isAuthenticated) return;

    const stepId = matchRoute(location.pathname);
    if (!stepId) return;

    const cached = queryClient.getQueryData<{ completedSteps: string[]; dismissed: boolean }>(CHECKLIST_QUERY_KEY);
    const alreadyCompleted = cached?.completedSteps?.includes(stepId);
    if (alreadyCompleted || pendingRef.current.has(stepId)) return;

    pendingRef.current.add(stepId);
    completeMutation.mutate(stepId);
    trackOnboardingStep(stepId, true);
  }, [location.pathname, isAuthenticated]);
}
